import { useState, useEffect, useCallback } from 'react';
import {useParams, Link, useLocation, useNavigate} from 'react-router-dom';
import { Button } from 'antd';
import ContentLayout from '../../../layout/ContentLayout';
import { Container } from '../../../components/content';
import GenericTable from '../../../components/tables/GenericTable';
import Pagination from '../../../components/layout/Pagination';
import { fetchUserOrders, exportOrders } from '../../../api/orders-api';
import ExportOrdersModal from '../../../components/modals/ExportOrdersModal';
import { usePermissions } from '../../../hooks/usePermissions';
import dayjs from 'dayjs';
import {formatOrderStatusAsPill} from "../../../utils/formatOrderStatus";

const UserOrders = () => {
    console.log("Component user orders is rendering")
    
    const { userId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { canAll } = usePermissions();
    // Same permissions as the /order/:id route and the API's /orders/export.
    const canManageOrders = canAll(['commerce:orders:read', 'scope:all']);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const { firstName, lastName, from: backTo } = location.state || {};
    const backHref = backTo || '/korisnici';
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalSum, setTotalSum] = useState(0);
    const [loading, setLoading] = useState(true);

    const columns = [
        {
            key: 'orderNumber',
            label: 'Broj narudžbine',
            render: (val, row) => row.orderNumber || '-',
        },
        {
            key: 'createdAt',
            label: 'Datum',
            render: (val, row) => dayjs(row.createdAt).format('DD.MM.YYYY.'),
        },
        {
            key: 'totalAmount',
            label: 'Ukupan iznos',
            render: (val, row) => `${row.total?.toFixed(2) || '0.00'}`,
        },
        {
            key: 'status',
            label: 'Status',
            render: (val, row) => formatOrderStatusAsPill(row.status),
        },
        {
            key: 'payment',
            label: 'ID Transakcije',
            render: (val, row) => <span>{row.payment?.transaction?.transId || '-'}</span>,
        },
        /*{
            key: 'paymentStatus',
            label: 'Status plaćanja',
            render: (val, row) => (
                <span className={row.paymentStatus === 'paid' ? 'active' : 'inactive'}>
          {row.paymentStatus === 'paid' ? 'Plaćeno' : 'Neplaćeno'}
        </span>
            ),
        },*/
    ];

    const getOrders = useCallback(async () => {
        setLoading(true);
        const { data, success, message } = await fetchUserOrders(userId, currentPage, 10);
     
        if (success) {
            setOrders(data.orders);
            setTotalPages(data.pagination.totalPages);
            setTotalItems(data.pagination.totalItems || 0);
            setTotalSum(data.ordersPriceSummary || 0);
        } else {
            console.log(message || 'Error fetching orders');
        }
        setLoading(false);
    }, [userId, currentPage]);

    useEffect(() => {
        getOrders();
    }, [getOrders]);

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    console.log('User orders:', orders);

    return (
        <Container>
            <Link to={backHref} className="link_btn" style={{ marginBottom: '12px'}}>
                <img alt="back" src="/assets/icons/chevron.svg" />
                <span>Nazad</span>
            </Link>

            <ContentLayout pt={true}>
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <p className="title">Porudžbine korisnika: {firstName} {lastName}</p>
                        {canManageOrders && (
                            <Button
                                className="btn-tertiary"
                                onClick={() => setIsExportModalOpen(true)}
                                icon={
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M8 12L3 7h3V1h4v6h3L8 12z" />
                                        <path d="M1 14h14v2H1z" />
                                    </svg>
                                }
                            >
                                Izvezi
                            </Button>
                        )}
                    </div>

                    {loading ? (
                        <p>Učitavanje...</p>
                    ) : (
                        <>
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                marginBottom: '24px'
                            }}>
                                <div style={{
                                    flex: 1,
                                    padding: '20px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6c757d',
                                        marginBottom: '8px',
                                        fontWeight: '500'
                                    }}>
                                        Ukupan broj porudžbina
                                    </p>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '600',
                                        color: '#212529',
                                        margin: 0
                                    }}>
                                        {totalItems}
                                    </p>
                                </div>

                                <div style={{
                                    flex: 1,
                                    padding: '20px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6c757d',
                                        marginBottom: '8px',
                                        fontWeight: '500'
                                    }}>
                                        Ukupna vrednost porudžbina
                                    </p>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '600',
                                        color: '#212529',
                                        margin: 0
                                    }}>
                                        {totalSum.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                            <GenericTable
                                data={orders}
                                columns={columns}
                                actions={canManageOrders ? [
                                    {
                                        key: 'view',
                                        label: 'Vidi detalje',
                                        icon: <img src="/assets/icons/view-icon.svg" alt="View" />,
                                        // backState restores this page's own state (name, back link) on return.
                                        onClick: (order) =>
                                            navigate(`/order/${order._id}`, {
                                                state: { from: location.pathname, backState: location.state },
                                            }),
                                    },
                                ] : undefined}
                            />
                            <div style={{ marginBottom: '12px'}}/>
                            {orders?.length > 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onPageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </ContentLayout>
            <ExportOrdersModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={(params) => exportOrders({ ...params, userId })}
            />
        </Container>
    );
};

export default UserOrders;